import { Trophy } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const TAG_STYLES = {
  'K.O.': { bg: 'rgba(220,38,38,0.85)', color: '#fff' },
  'M.E.': { bg: 'rgba(22,163,74,0.85)', color: '#fff' },
  'Multi day': { bg: 'rgba(59,130,246,0.75)', color: '#fff' },
  'Turbo': { bg: 'rgba(234,179,8,0.8)', color: '#000' },
  'Hyper Turbo': { bg: 'rgba(249,115,22,0.85)', color: '#000' },
  'Super Hyper Turbo': { bg: 'rgba(249,115,22,0.85)', color: '#000' },
  'Satellite': { bg: 'rgba(139,92,246,0.8)', color: '#fff' },
  'Stack Choice': { bg: 'rgba(236,72,153,0.75)', color: '#fff' },
};

export const ScheduleTable = ({ tournaments, loading, selectedDate, compact = false }) => {
  const formatDateHeading = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Convert reg_close_time ("YYYY-MM-DD HH:MM") into "HH:MM" or "HH:MM +Nd"
  // relative to the event's start date. Used to display the exact closing
  // timestamp under a RUNNING badge.
  const formatCloseSubtext = (t) => {
    if (!t || !t.reg_close_time) return null;
    const [datePart, timePart] = t.reg_close_time.split(' ');
    if (!timePart) return null;
    if (t.date && datePart && datePart !== t.date) {
      const start = new Date(t.date + 'T00:00:00');
      const close = new Date(datePart + 'T00:00:00');
      const diff = Math.round((close - start) / 86400000);
      if (diff > 0) return `${timePart} +${diff}d`;
      if (diff < 0) return `${timePart} ${diff}d`;
    }
    return timePart;
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={`skeleton-${i}`} className="h-16 w-full rounded-lg" style={{ background: '#140a0a' }} />)}</div>
      </div>
    );
  }

  if (!tournaments || tournaments.length === 0) {
    return (
      <div data-testid="schedule-empty" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <Trophy className="w-10 h-10 text-white/20 mx-auto mb-3" />
        <p className="font-heading text-lg text-white/30 uppercase tracking-wider">No tournaments scheduled</p>
      </div>
    );
  }

  return (
    <section data-testid="schedule-section" className={compact ? "w-full" : "max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10"}>
      {/* Section header — hidden in compact mode (kiosk billboard already shows it) */}
      {!compact && (
        <div className="flex items-end justify-between mb-5">
          <div>
            <h3 data-testid="schedule-date-title" className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">
              Daily Schedule
            </h3>
            <p className="font-body text-sm text-primary-main/80 mt-1">{formatDateHeading(selectedDate)}</p>
          </div>
          <span className="font-mono text-xs text-white/20">{tournaments.length} events</span>
        </div>
      )}

      {/* Table */}
      <div data-testid="schedule-table" className="w-full overflow-x-auto rounded-xl" style={{ background: 'rgba(14,7,7,0.6)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <table className="w-full min-w-[960px] border-collapse">
          {/* Header */}
          <thead>
            <tr style={{ background: 'linear-gradient(90deg, rgba(185,28,28,0.15) 0%, rgba(212,175,55,0.08) 50%, rgba(185,28,28,0.15) 100%)' }}>
              <th className="px-5 py-3 text-left w-[8%]"><span className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase font-bold" style={{ color: '#facc15' }}>Time</span></th>
              <th className="px-3 py-3 text-left w-[5%]"><span className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase font-bold" style={{ color: '#facc15' }}>#</span></th>
              <th className="px-4 py-3 text-left w-[32%]"><span className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase font-bold" style={{ color: '#facc15' }}>Event</span></th>
              <th className="px-4 py-3 text-left w-[22%]"><span className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase font-bold" style={{ color: '#facc15' }}>Buy-in</span></th>
              <th className="px-4 py-3 text-center w-[9%]"><span className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase font-bold" style={{ color: '#facc15' }}>Stack</span></th>
              <th className="px-4 py-3 text-center w-[8%]"><span className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase font-bold" style={{ color: '#facc15' }}>Duration</span></th>
              <th className="px-4 py-3 text-center w-[8%]"><span className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase font-bold whitespace-nowrap" style={{ color: '#facc15' }}>Reg Close</span></th>
              <th className="px-4 py-3 text-center w-[9%]"><span className="font-heading text-sm sm:text-base tracking-[0.15em] uppercase font-bold" style={{ color: '#facc15' }}>Entries</span></th>
            </tr>
          </thead>
          <tbody>
            {tournaments.map((t, idx) => {
              const isPremium = t.is_premium;
              const isEven = idx % 2 === 0;

              let rowBg;
              if (isPremium) {
                rowBg = 'rgba(25,20,8,0.65)';
              } else if (isEven) {
                rowBg = 'rgba(10,5,5,0.6)';
              } else {
                rowBg = 'rgba(14,7,7,0.6)';
              }

              return (
                <tr
                  key={t.id}
                  data-testid={`schedule-row-${t.id}`}
                  className="group transition-colors duration-200 hover:!bg-white/[0.03]"
                  style={{
                    background: rowBg,
                    borderBottom: '1px solid rgba(255,255,255,0.03)',
                    borderLeft: isPremium ? '3px solid #d4af37' : '3px solid transparent',
                  }}
                >
                  {/* TIME */}
                  <td className="px-5 py-4 align-middle">
                    <span className="font-heading text-lg font-bold" style={{ color: '#ff3b3b' }}>{t.time}</span>
                  </td>

                  {/* # */}
                  <td className="px-3 py-4 align-middle">
                    <span className="font-heading text-sm text-white font-semibold">{t.event_number}</span>
                  </td>

                  {/* EVENT */}
                  <td className="px-4 py-4 align-middle">
                    <div>
                      <p className="font-heading text-sm sm:text-[15px] text-white font-bold uppercase tracking-wide leading-snug">{t.event_name}</p>
                      {t.tags && t.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {t.tags.map((tag) => {
                            const style = TAG_STYLES[tag] || TAG_STYLES['M.E.'];
                            return (
                              <span key={tag} className="inline-block px-2 py-[1px] rounded text-[9px] font-bold uppercase tracking-wider" style={{ background: style.bg, color: style.color }}>
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* BUY-IN */}
                  <td className="px-4 py-4 align-middle">
                    {t.buyin ? (
                      <div>
                        <span className="font-mono text-sm font-bold" style={{ color: '#facc15' }}>
                          {t.buyin}
                        </span>
                        {t.buyin_breakdown && (
                          <span className="block font-mono text-[11px] text-white/90 mt-0.5">({t.buyin_breakdown})</span>
                        )}
                        {t.bounty && (
                          <span className="block font-mono text-[11px] mt-0.5" style={{ color: '#ff3b3b' }}>({t.bounty})</span>
                        )}
                        {t.guaranteed && (
                          <span className="block font-mono text-[10px] mt-1 font-bold" style={{ color: '#facc15' }}>{t.guaranteed}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-white/15 text-sm">-</span>
                    )}
                  </td>

                  {/* STACK */}
                  <td className="px-4 py-4 align-middle text-center">
                    <span className="font-mono text-sm text-white">{t.stack || '-'}</span>
                  </td>

                  {/* DURATION */}
                  <td className="px-4 py-4 align-middle text-center">
                    <span className="font-body text-sm text-white">{t.duration || '-'}</span>
                  </td>

                  {/* REG CLOSE */}
                  <td className="px-4 py-4 align-middle text-center">
                    {t.reg_close === 'FINISHED' ? (
                      <span className="font-heading text-sm font-bold" style={{ color: '#facc15' }}>FINISHED</span>
                    ) : t.reg_close === 'RUNNING' ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="inline-flex items-center gap-1.5 font-heading text-sm text-emerald-400 font-bold">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                          </span>
                          RUNNING
                        </span>
                        {formatCloseSubtext(t) && (
                          <span className="font-body text-[11px] tracking-wide" style={{ color: '#facc15' }}>
                            closes {formatCloseSubtext(t)}
                          </span>
                        )}
                      </div>
                    ) : t.reg_close === 'CLOSED' ? (
                      <span className="font-heading text-sm font-bold" style={{ color: '#ff3b3b' }}>CLOSED</span>
                    ) : (
                      <span className="font-body text-sm text-white">{t.reg_close || '-'}</span>
                    )}
                  </td>

                  {/* ENTRIES: players/entries — with winner detail on finished */}
                  <td className="px-4 py-4 align-middle text-center">
                    {(() => {
                      const players = t.total_players || 0;
                      const entries = t.total_entries || t.total_entries_day || 0;
                      const finished = t.reg_close === 'FINISHED';
                      const hasWinner = t.winner_name || t.winner_prize;
                      const hasData = players > 0 || entries > 0;
                      const gtd = t.guaranteed || '';

                      const playersEntries = hasData ? (
                        <span className="font-mono text-sm font-bold">
                          <span style={{ color: '#facc15' }}>{players}</span>
                          <span className="text-white mx-1">/</span>
                          <span className="text-white">{entries}</span>
                        </span>
                      ) : gtd && gtd !== 'None' ? (
                        // No live registrations yet — surface the Guarantee so the
                        // column always carries useful info.
                        <div className="leading-tight">
                          <div className="font-heading text-[11px] font-bold uppercase tracking-widest" style={{ color: '#facc15' }}>GTD</div>
                          <div className="font-mono text-sm font-bold" style={{ color: '#facc15' }}>{gtd}</div>
                        </div>
                      ) : (
                        // No data at all — show TBA in gold instead of an empty dash.
                        <span className="font-heading text-[11px] font-bold uppercase tracking-widest" style={{ color: '#facc15' }}>TBA</span>
                      );

                      if (finished && hasWinner) {
                        return (
                          <div className="leading-tight">
                            <div className="font-heading text-[11px] font-bold uppercase tracking-wide" style={{ color: '#facc15' }}>
                              {'\u{1F3C6}'} {t.winner_name}
                            </div>
                            {t.winner_prize && (
                              <div className="font-mono text-[10px] text-white mt-0.5">{t.winner_prize}</div>
                            )}
                            {hasData && <div className="mt-1">{playersEntries}</div>}
                          </div>
                        );
                      }
                      return playersEntries;
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bottom accent */}
      <div className="mt-3 flex items-center justify-between">
        <div className="h-px flex-1 bg-gradient-to-r from-secondary-main/20 via-primary-main/10 to-transparent" />
        <span className="font-mono text-[10px] text-primary-main/30 px-4 tracking-widest">RDPT PLUS+ 2026</span>
        <div className="h-px flex-1 bg-gradient-to-l from-secondary-main/20 via-primary-main/10 to-transparent" />
      </div>
    </section>
  );
};
